import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Chip, Stack } from '@mui/material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine } from 'recharts';
import { useExpensesYear } from '../../../context/ExpensesYearContext';
import { ExpenseType, CategoryMonthStatDto } from '../../../types/homeExpenses';
import { formatEuro } from '../../../utils/currency';
import { formatDate } from '../../../utils/date';
import { useCategoryMonthBreakdown } from '../../../hooks/useHomeExpenses';

interface Props {
  expenseType?: ExpenseType;
}

const monthLabels = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const CategoryMonthComparisonChart: React.FC<Props> = ({ expenseType = ExpenseType.Home }) => {
  const { year } = useExpensesYear();
  const { data, loading, error } = useCategoryMonthBreakdown(year, expenseType);
  const [selected, setSelected] = useState<{ month: number; category: string; items: CategoryMonthStatDto['overstepExpenses'] } | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const chartData = useMemo(() => {
    // Build stacked dataset: one entry per month with category totals
    const byMonth: Record<number, any> = {};
    data.filter(d => !d.isIncomeCategory).forEach(d => {
      const m = d.month; if (!byMonth[m]) byMonth[m] = { month: monthLabels[m-1], Total: 0 };
      byMonth[m][d.categoryName] = d.totalAmount;
      byMonth[m].Total += d.totalAmount || 0;
      byMonth[m][`${d.categoryName}__meta`] = {
        avg: d.averageAmount,
        budget: d.budgetAmount,
        prevAvg: d.previousYearAverageAmount,
        over: d.overAverage,
      };
    });
    return Array.from({ length: 12 }, (_, i) => byMonth[i+1] || { month: monthLabels[i] });
  }, [data]);

  const categoryColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    data.filter(d => !d.isIncomeCategory).forEach(d => {
      if (!map[d.categoryName]) map[d.categoryName] = d.categoryColor || '#8884d8';
    });
    return map;
  }, [data]);

  const categoryAvgMap = useMemo(() => {
    const map: Record<string, number> = {};
    data.filter(d => !d.isIncomeCategory).forEach(d => {
      if (map[d.categoryName] == null) map[d.categoryName] = d.averageAmount || 0;
    });
    return map;
  }, [data]);

  const categoryBudgetMap = useMemo(() => {
    const map: Record<string, number | undefined> = {};
    data.filter(d => !d.isIncomeCategory).forEach(d => {
      if (map[d.categoryName] == null) {
        const b = d.budgetAmount;
        map[d.categoryName] = b && b > 0 ? b : undefined;
      }
    });
    return map;
  }, [data]);

  const totalAverageAcrossYear = useMemo(() => {
    const monthlyTotals = Array.from({ length: 12 }, () => 0);
    data.filter(d => !d.isIncomeCategory).forEach(d => {
      monthlyTotals[d.month - 1] += d.totalAmount || 0;
    });
    const sum = monthlyTotals.reduce((a, b) => a + b, 0);
    return sum / 12;
  }, [data]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    data.filter(d => !d.isIncomeCategory).forEach(d => set.add(d.categoryName));
    return Array.from(set.values()).sort();
  }, [data]);

  const density = useMemo(() => {
    const count = categories.length;
    if (count >= 12) return { barCategoryGap: '8%', barGap: 1, maxBarSize: 12 };
    if (count >= 8) return { barCategoryGap: '12%', barGap: 2, maxBarSize: 14 };
    if (count >= 5) return { barCategoryGap: '16%', barGap: 3, maxBarSize: 18 };
    return { barCategoryGap: '20%', barGap: 4, maxBarSize: 22 };
  }, [categories.length]);

  const handleBarClick = (payload: any, index: number, category: string) => {
    try {
      const monthIndex = index + 1;
      const found = data.find(d => d.month === monthIndex && d.categoryName === category);
      if (found && found.overAverage && found.overstepExpenses?.length) {
        setSelected({ month: monthIndex, category, items: found.overstepExpenses });
      }
    } catch {}
  };

  const formatCurrency = formatEuro;

  const renderTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const isTotal = hoveredCategory === 'Total';
      const entry = isTotal
        ? payload.find((e: any) => e.dataKey === 'Total')
        : (hoveredCategory
            ? payload.find((e: any) => e.dataKey === hoveredCategory)
            : payload.find((e: any) => e.dataKey && e.dataKey !== 'Total')) || payload[0];
      const category = isTotal ? 'Total' : entry?.dataKey;
      const meta = isTotal ? null : entry?.payload?.[`${category}__meta`];
      const val = Number(entry?.value || 0);
      const avg = isTotal ? Number(totalAverageAcrossYear || 0) : Number(meta?.avg || 0);
      const budget = Number(meta?.budget || 0);
      const prevAvg = Number(meta?.prevAvg || 0);
      const threshold = isTotal ? avg : (budget > 0 ? budget : (prevAvg > 0 ? prevAvg : avg));
      const underAvg = val <= threshold;
      return (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2">{year} – {label}</Typography>
          <Typography variant="body2">Categoria: <strong>{category}</strong></Typography>
          <Typography variant="body2" sx={{ color: underAvg ? 'success.main' : 'error.main' }}>
            Total: {formatCurrency(val)}
          </Typography>
          {!isTotal && budget > 0 && (
            <Typography variant="body2">Orçamento: {formatCurrency(budget)} {val > budget ? '(excedido)' : ''}</Typography>
          )}
          {!isTotal && budget <= 0 && prevAvg > 0 && (
            <Typography variant="body2">Média do ano anterior: {formatCurrency(prevAvg)} {val > prevAvg ? '(excedido)' : ''}</Typography>
          )}
          <Typography variant="body2">Média: {formatCurrency(avg)} {val > avg ? '(acima)' : ''}</Typography>
          {!isTotal && (
            <Typography variant="caption" sx={{ display:'block', mt:1 }}>Clique numa barra para ver os detalhes acima da média.</Typography>
          )}
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>A carregar análise mensal por categoria…</Typography>
      </Paper>
    );
  }
  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb:0 }}>
            📋 Mensal por categoria (despesas)
            </Typography>
            <Chip label={year} size="small" color="primary" variant="outlined" />
        </Box>
      <Paper sx={{ p: 2, height: 420 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
            barCategoryGap={density.barCategoryGap}
            barGap={density.barGap}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(v) => formatCurrency(v).replace("€", "").trim()} />
            <Tooltip content={renderTooltip} />
            {/* Custom Legend including average line hints */}
            <Legend content={(props: any) => {
              const payload = props && props.payload ? props.payload : [];
              return (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    {payload.map((item: any) => (
                      <Box key={item.value} sx={{ display:'flex', alignItems:'center', mr: 2 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: item.color || '#8884d8', mr: 0.5 }} />
                        <Typography variant="caption">{item.value}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={2} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }} alignItems="center">
                    <Box sx={{ width: 24, borderTop: '2px dashed', borderColor: 'rgba(0,0,0,0.6)' }} />
                    <Typography variant="caption">Tracejado cinzento = média total</Typography>
                    <Box sx={{ width: 24, borderTop: '2px dashed', borderColor: 'primary.main' }} />
                    <Typography variant="caption">Tracejado colorido = média da categoria</Typography>
                    <Box sx={{ width: 24, borderTop: '2px solid', borderColor: 'secondary.main' }} />
                    <Typography variant="caption">Contínuo colorido = orçamento da categoria</Typography>
                  </Stack>
                </Box>
              );
            }} />
            {/* Total average line across all categories (render before bars to sit behind) */}
            <ReferenceLine
              y={totalAverageAcrossYear || 0}
              stroke="rgba(0,0,0,0.6)"
              strokeOpacity={0.4}
              strokeDasharray="6 3"
              strokeWidth={2}
            />
            {/* Monthly total column (render before category bars) */}
            <Bar
              dataKey="Total"
              fill="#9e9e9e"
              fillOpacity={0.35}
              name="Total"
              onMouseOver={() => setHoveredCategory('Total')}
              onMouseOut={() => setHoveredCategory(null)}
              maxBarSize={density.maxBarSize}
            />
            {categories.map(cat => (
              <Bar
                key={cat}
                dataKey={cat}
                fill={categoryColorMap[cat] || '#8884d8'}
                maxBarSize={density.maxBarSize}
                onClick={(payload, idx) => handleBarClick(payload, idx, cat)}
                onMouseOver={() => setHoveredCategory(cat)}
                onMouseOut={() => setHoveredCategory(null)}
              />
            ))}

            {/* Per-category average lines */}
            {categories.map(cat => (
              <ReferenceLine key={`avg-${cat}`} y={categoryAvgMap[cat] || 0} stroke={categoryColorMap[cat] || '#8884d8'} strokeDasharray="5 5" strokeWidth={1} />
            ))}

            {/* Per-category budget lines (solid) */}
            {categories.map(cat => (
              categoryBudgetMap[cat] != null ? (
                <ReferenceLine
                  key={`budget-${cat}`}
                  y={categoryBudgetMap[cat] as number}
                  stroke={categoryColorMap[cat] || '#8884d8'}
                  strokeWidth={2}
                  strokeOpacity={0.9}
                />
              ) : null
            ))}

            {/* Total average line moved above to layer behind bars */}
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Detalhes acima da média – {selected ? `${monthLabels[(selected.month||1)-1]} / ${selected.category}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {selected && selected.items.length ? (
            <List>
              {selected.items.map(item => (
                <ListItem key={item.publicId} sx={{ display:'flex', justifyContent:'space-between' }}>
                  <ListItemText primary={formatDate(item.date)} secondary={item.observations || ''} />
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Chip size="small" label={item.groupName} />
                    <Chip size="small" label={item.accountName} variant="outlined" />
                    <Typography color="error.main" sx={{ minWidth: 120, textAlign:'right' }}>{formatCurrency(item.amount)}</Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">Sem detalhes disponíveis.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryMonthComparisonChart;
